import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import ActivityFeed from '@/components/organisms/Tile/TileActivityLog/ActivityFeed/ActivityFeed';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useActivityLogs } from '@/hooks/useActivityLogs';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { selectBrainId } from '@/redux/session/selectors';

import { LocalDateFilter } from './LocalDateFilter';

export const OverviewActivity = () => {
  const { t } = useTranslation();
  const { slug: accountSlug } = useParams();
  const brainId = useSelector(selectBrainId);

  // Date selection persisted to localStorage; range is re-resolved against today on each render.
  const { dateRange, selection, setSelection } = usePersistedDateRange(
    'ai_agent_activity_date_filter'
  );

  // Get activity logs for this brain with local date state
  const {
    data: activityData,
    isLoading: activityLoading,
    fetchNextPage,
    hasNextPage,
  } = useActivityLogs(accountSlug, {
    brainId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const allLogs = useMemo(() => {
    return activityData?.pages?.flatMap((page) => page.audit_logs) || [];
  }, [activityData]);

  const handleShowMore = () => {
    fetchNextPage();
  };

  return (
    <Card className="w-full gap-0 rounded-md border-border bg-background p-0 text-foreground shadow-none">
      <CardHeader className="flex flex-row items-center justify-between p-5">
        <h3 className="text-base font-semibold">{t('common.activity')}</h3>
        <LocalDateFilter selection={selection} onSelectionChange={setSelection} align="end" />
      </CardHeader>
      <CardContent className="flex flex-col gap-6 px-5 pb-5">
        <ActivityFeed
          logs={allLogs}
          hideAgentLink
          isLoading={activityLoading}
          emptyMessage={t('activityLogs.noLogs')}
          onShowMore={handleShowMore}
          hasMore={hasNextPage}
        />
      </CardContent>
    </Card>
  );
};
